import { z } from 'zod';

/** Uma escala (parada) do segmento — o aeroporto de conexão entre duas pernas
 *  + o tempo de espera até o próximo voo. Alimenta os nós da linha do tempo do
 *  card de ofertas (ícone + IATA + layover). Display-only; presente apenas nas
 *  ofertas curadas (o produtor busca as pernas eager só pra elas). */
export const ChatUiFlightConnectionSchema = z.object({
  /** IATA do aeroporto de conexão (destino de uma perna = origem da próxima). */
  iata: z.string().min(1),
  /** Layover formatado ("1h 25m") — derivado no produtor. Ausente quando o
   *  tempo de espera não veio do gateway. */
  layoverLabel: z.string().optional(),
});
export type ChatUiFlightConnection = z.infer<typeof ChatUiFlightConnectionSchema>;

/** Display segment shared across the flight cards (offers, quote, reservation,
 *  confirmation summary). Date-time fields are plain strings: the http flight
 *  provider does not guarantee ISO format, so clients format defensively. */
export const ChatUiFlightSegmentSchema = z.object({
  from: z.string().min(1),
  to: z.string().min(1),
  departAt: z.string(),
  arriveAt: z.string(),
  carrier: z.string(),
  flightNumber: z.string(),
  // Duração derivada dos horários do segmento (produtor) — "2h 05m". Opcional:
  // ausente quando os horários não parseiam. Display-only.
  durationLabel: z.string().optional(),
  // Nome de exibição da companhia (upstream `company.fullName`) — chips de
  // companhia da drawer de filtro + linha do voo. Pode chegar vazio; resolva
  // sempre por `carrierDisplayName`, que degrada para o código `carrier`.
  carrierName: z.string().optional(),
  // Duração numérica em minutos — alimenta a ordenação "mais rápido" da drawer
  // (o label string não é confiável para ordenar). Opcional → fora dessa ordenação.
  durationMinutes: z.number().int().nonnegative().optional(),
  // Escalas do trecho (aeroporto de conexão + layover) — nós da linha do tempo
  // no card de ofertas. Presente só nas ofertas curadas (o produtor busca as
  // pernas eager só pra elas); ausente → o card cai no texto de contagem
  // (Direto / N paradas). Display-only.
  connections: z.array(ChatUiFlightConnectionSchema).optional(),
  // Aeronave da malha publicada (Cirium `aircraftName`, «Embraer 195 E2») — o
  // cabeçalho do trecho no card 1click, depois do nº do voo. Só o checkout a
  // preenche, e só no trecho DIRETO cuja oferta o agente enriqueceu; ausente →
  // o cabeçalho fica só com o voo. Display-only.
  aircraftName: z.string().min(1).optional(),
});
export type ChatUiFlightSegment = z.infer<typeof ChatUiFlightSegmentSchema>;

/** Nome de exibição da companhia de um trecho: `carrierName` aparado quando tem
 *  conteúdo, senão o código `carrier` aparado, senão vazio. `carrierName: ''`
 *  chega do backend e `??` não degradaria — por isso o teste de conteúdo.
 *  Aceita segmento ausente porque os call sites resolvem `segments[0]`. */
export function carrierDisplayName(
  seg: { carrier?: string; carrierName?: string } | null | undefined,
): string {
  return seg?.carrierName?.trim() || seg?.carrier?.trim() || '';
}
